import { DateTime } from 'luxon';
import { useOverviewDashboardStore } from 'pages/ReportsPage/components/AnalyticsDashboards/OverviewDashboard/store/overviewDashboard.store';
import { useAnalyticsFilterStore } from 'pages/ReportsPage/store/analyticsFilterView.context';
import { KpiStatCardProps } from 'pages/ReportsPage/components/AnalyticsDashboards/KpiStatCard';
import { formatDashboardNumber } from 'pages/ReportsPage/components/AnalyticsDashboards/formatDashboardNumber';
import { isSelectionCleared } from 'pages/ReportsPage/store/analyticsSelection.utils';
import { buildClearedCard } from 'pages/ReportsPage/components/AnalyticsDashboards/kpiCardBuilders';

function formatPriorRange(rangeStart: string, periodDays: number | null): string | undefined {
  if (periodDays == null || periodDays <= 0) return undefined;
  const start = DateTime.fromISO(rangeStart, { setZone: true });
  if (!start.isValid) return undefined;
  const priorStart = start.minus({ days: periodDays });
  const priorEnd = start.minus({ days: 1 });
  const fmt = (d: DateTime) => d.toFormat('MMM d');
  return `vs. ${fmt(priorStart)} – ${fmt(priorEnd)}`;
}

export function useOverviewKpiCards(): KpiStatCardProps[] {
  const locationIds = useAnalyticsFilterStore((s) => s.locationIds);
  const positions = useAnalyticsFilterStore((s) => s.positions);
  const rangeStart = useAnalyticsFilterStore((s) => s.dateRange[0]);
  const totalShifts = useOverviewDashboardStore((s) => s.totalShifts);
  const periodDays = useOverviewDashboardStore((s) => s.periodDays);
  const changePrct = useOverviewDashboardStore((s) => s.changePrct);
  const totalShiftsError = useOverviewDashboardStore((s) => s.totalShiftsError);
  const fulfillmentPrctTotal = useOverviewDashboardStore((s) => s.fulfillmentPrctTotal);
  const fulfillmentPrctPeriodDays = useOverviewDashboardStore((s) => s.fulfillmentPrctPeriodDays);
  const fulfillmentPrctChangePrct = useOverviewDashboardStore((s) => s.fulfillmentPrctChangePrct);
  const fulfillmentPrctError = useOverviewDashboardStore((s) => s.fulfillmentPrctError);
  const calloutHoursTotal = useOverviewDashboardStore((s) => s.calloutHoursTotal);
  const calloutHoursPeriodDays = useOverviewDashboardStore((s) => s.calloutHoursPeriodDays);
  const calloutHoursChangePrct = useOverviewDashboardStore((s) => s.calloutHoursChangePrct);
  const calloutHoursError = useOverviewDashboardStore((s) => s.calloutHoursError);
  const activeTeamMembersTotal = useOverviewDashboardStore((s) => s.activeTeamMembersTotal);
  const activeTeamMembersPeriodDays = useOverviewDashboardStore(
    (s) => s.activeTeamMembersPeriodDays,
  );
  const activeTeamMembersChangePrct = useOverviewDashboardStore(
    (s) => s.activeTeamMembersChangePrct,
  );
  const activeTeamMembersError = useOverviewDashboardStore((s) => s.activeTeamMembersError);

  const totalShiftsComparison = formatPriorRange(rangeStart, periodDays);
  const fulfillmentComparison = formatPriorRange(rangeStart, fulfillmentPrctPeriodDays);
  const calloutComparison = formatPriorRange(rangeStart, calloutHoursPeriodDays);
  const activeTeamMembersComparison = formatPriorRange(rangeStart, activeTeamMembersPeriodDays);

  const cards: KpiStatCardProps[] = [
    totalShiftsError
      ? {
          label: 'Total Shifts',
          value: '--',
          subtitle: 'Failed to load',
          subtitleVariant: 'error' as const,
        }
      : {
          label: 'Total Shifts',
          value: totalShifts.toLocaleString(),
          ...(changePrct != null && {
            changePercentage: changePrct,
            comparisonText: totalShiftsComparison,
          }),
        },
    fulfillmentPrctError
      ? {
          label: 'Fulfillment percentage',
          value: '--',
          subtitle: 'Failed to load',
          subtitleVariant: 'error' as const,
        }
      : {
          label: 'Fulfillment percentage',
          value: `${formatDashboardNumber(fulfillmentPrctTotal)}%`,
          ...(fulfillmentPrctChangePrct != null && {
            changePercentage: fulfillmentPrctChangePrct,
            comparisonText: fulfillmentComparison,
          }),
        },
    calloutHoursError
      ? {
          label: 'Call out hours',
          value: '--',
          subtitle: 'Failed to load',
          subtitleVariant: 'error' as const,
        }
      : {
          label: 'Call out hours',
          value: formatDashboardNumber(calloutHoursTotal),
          ...(calloutHoursChangePrct != null && {
            changePercentage: calloutHoursChangePrct,
            comparisonText: calloutComparison,
          }),
        },
    activeTeamMembersError
      ? {
          label: 'Active team members',
          value: '--',
          subtitle: 'Failed to load',
          subtitleVariant: 'error' as const,
        }
      : {
          label: 'Active team members',
          value: activeTeamMembersTotal.toLocaleString(),
          ...(activeTeamMembersChangePrct != null && {
            changePercentage: activeTeamMembersChangePrct,
            comparisonText: activeTeamMembersComparison,
          }),
        },
  ];

  return isSelectionCleared(locationIds, positions)
    ? cards.map((card) => buildClearedCard(card.label))
    : cards;
}
